/**
 * Reports and complaints management (requirement 7).
 * Shows the 7-business-day deadline, highlights overdue / due-soon items, filters,
 * registers complaints received outside the service and records reporting to the acquirer.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiClient, type ComplaintChannel, type ContentReport, type ReportStatus } from '../lib/api-client';
import { formatJstDateTime, jstLocalInputToIso, nowAsJstLocalInput, parseApiTimestamp } from '../lib/date-utils';
import Pagination from './Pagination';
import ExternalComplaintForm from './moderation/ExternalComplaintForm';
import {
  CHANNEL_LABELS,
  COMPLAINANT_LABELS,
  DUE_STATE_LABELS,
  REPORT_REASON_LABELS,
  REPORT_STATUS_LABELS,
  SUBJECT_TYPE_LABELS,
} from './moderation/moderationLabels';

type ListMode = 'all' | 'open' | 'overdue';

const DUE_STATE_CLASSES: Record<ContentReport['due_state'], string> = {
  overdue: 'border-red-600 bg-red-50',
  due_soon: 'border-yellow-500 bg-yellow-50',
  on_track: 'border-gray-300',
  closed: 'border-gray-200 bg-gray-50',
};

const STATUSES = Object.keys(REPORT_STATUS_LABELS) as ReportStatus[];

export default function ReportsManagementPanel() {
  const [reports, setReports] = useState<ContentReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [mode, setMode] = useState<ListMode>('open');
  const [channel, setChannel] = useState<ComplaintChannel | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiClient.listContentReports(undefined, limit, (page - 1) * limit, {
        openOnly: mode === 'open',
        overdueOnly: mode === 'overdue',
        channel: channel || undefined,
      });
      setReports(result.reports);
      setTotal(result.total);
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [limit, page, mode, channel]);

  useEffect(() => { void load(); }, [load]);

  const update = async (report: ContentReport, status: ReportStatus, note: string, acquirerReportedAt: string | null | undefined) => {
    try {
      await apiClient.updateContentReport(report.id, status, note, acquirerReportedAt);
      setNotice(`#${report.id} を保存しました`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : '更新に失敗しました');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <section className="p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold">通報・苦情管理</h2>
          <p className="mt-1 text-sm text-gray-600">受付から7営業日（土日・祝日を除く）以内に調査し、結果をカード会社へ報告してください。</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowForm((value) => !value)} className="bg-pink-600 px-4 py-2 text-white">
            {showForm ? '登録フォームを閉じる' : '外部の苦情を登録'}
          </button>
          <button onClick={() => void load()} className="bg-gray-700 px-4 py-2 text-white">更新</button>
        </div>
      </div>

      {showForm && (
        <div className="mb-6">
          <ExternalComplaintForm
            onCreated={async (report) => {
              setShowForm(false);
              setNotice(`#${report.id} を登録しました（期限 ${formatJstDateTime(report.due_at)}）`);
              await load();
            }}
          />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
        <div role="group" aria-label="表示する通報" className="flex">
          {([['open', '未解決のみ'], ['overdue', '期限超過のみ'], ['all', 'すべて']] as const).map(([value, label]) => (
            <button
              key={value} type="button" aria-pressed={mode === value}
              onClick={() => { setMode(value); setPage(1); }}
              className={`border px-3 py-1 ${mode === value ? 'border-red-700 bg-red-700 text-white' : 'border-gray-300 bg-white'}`}
            >
              {label}
            </button>
          ))}
        </div>
        <label>
          受付経路
          <select
            value={channel} onChange={(e) => { setChannel(e.target.value as ComplaintChannel | ''); setPage(1); }}
            className="ml-2 border border-gray-300 p-1"
          >
            <option value="">すべて</option>
            {(Object.keys(CHANNEL_LABELS) as ComplaintChannel[]).map((value) => (
              <option key={value} value={value}>{CHANNEL_LABELS[value]}</option>
            ))}
          </select>
        </label>
      </div>

      {error && <p className="mb-4 text-red-700" role="alert">{error}</p>}
      {notice && <p className="mb-4 bg-green-50 p-2 text-sm text-green-800" role="status">{notice}</p>}
      {loading ? <p>読み込み中...</p> : reports.length === 0 ? <p>該当する通報・苦情はありません。</p> : (
        <div className="space-y-4">
          {reports.map((report) => <ReportCard key={report.id} report={report} onUpdate={update} />)}
        </div>
      )}
      {!loading && (
        <Pagination
          currentPage={page} totalPages={totalPages} totalItems={total} itemsPerPage={limit} onPageChange={setPage}
          onItemsPerPageChange={(nextLimit) => { setLimit(nextLimit); setPage(1); }}
        />
      )}
    </section>
  );
}

function toJstLocalInput(value: string | null): string {
  if (!value) return '';
  return nowAsJstLocalInput(parseApiTimestamp(value));
}

function DeadlineBadge({ report }: { report: ContentReport }) {
  const base = 'px-2 py-0.5 text-xs font-semibold';
  if (report.due_state === 'closed') return <span className={`${base} bg-gray-200 text-gray-700`}>{DUE_STATE_LABELS.closed}</span>;
  if (report.due_state === 'overdue') return <span className={`${base} bg-red-700 text-white`}>{DUE_STATE_LABELS.overdue}</span>;
  const tone = report.due_state === 'due_soon' ? 'bg-yellow-500 text-white' : 'bg-green-100 text-green-800';
  return <span className={`${base} ${tone}`}>{DUE_STATE_LABELS[report.due_state]}（残り{report.business_days_remaining}営業日）</span>;
}

function ReportCard({ report, onUpdate }: {
  report: ContentReport;
  onUpdate: (report: ContentReport, status: ReportStatus, note: string, acquirerReportedAt: string | null | undefined) => Promise<void>;
}) {
  const [status, setStatus] = useState<ReportStatus>(report.status);
  const [note, setNote] = useState(report.resolution_note || '');
  const [reportedAt, setReportedAt] = useState(toJstLocalInput(report.acquirer_reported_at));
  const [saving, setSaving] = useState(false);
  const initialReportedAt = toJstLocalInput(report.acquirer_reported_at);

  const save = async () => {
    setSaving(true);
    const acquirerValue = reportedAt === initialReportedAt ? undefined : reportedAt ? jstLocalInputToIso(reportedAt) : null;
    await onUpdate(report, status, note, acquirerValue);
    setSaving(false);
  };

  const reporter = report.channel === 'in_app'
    ? `${report.reporter_username ?? '—'} (ID: ${report.reporter_user_id ?? '—'})`
    : `${report.complainant_name || '（名称なし）'}`;

  return (
    <article className={`border-l-4 border p-4 ${DUE_STATE_CLASSES[report.due_state]}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-bold">
          #{report.id} {SUBJECT_TYPE_LABELS[report.subject_type]} / {report.streamer_username ?? '対象配信者なし'}
        </h3>
        <DeadlineBadge report={report} />
      </div>
      <dl className="mt-3 grid gap-2 text-sm md:grid-cols-[9rem_1fr]">
        <dt className="font-semibold">受付日時</dt><dd>{formatJstDateTime(report.received_at)}</dd>
        <dt className="font-semibold">対応期限</dt><dd>{formatJstDateTime(report.due_at)}</dd>
        <dt className="font-semibold">受付経路</dt><dd>{CHANNEL_LABELS[report.channel]}（{COMPLAINANT_LABELS[report.complainant_type]}）</dd>
        <dt className="font-semibold">申立者</dt><dd>{reporter}</dd>
        <dt className="font-semibold">理由</dt><dd>{REPORT_REASON_LABELS[report.reason]}</dd>
        <dt className="font-semibold">詳細</dt><dd className="whitespace-pre-wrap">{report.details || '（記載なし）'}</dd>
        <dt className="font-semibold">配信キー</dt><dd>{report.stream_key || '—'}</dd>
        {report.resolved_at && (<><dt className="font-semibold">解決日時</dt><dd>{formatJstDateTime(report.resolved_at)}</dd></>)}
      </dl>
      <div className="mt-4 grid gap-3 md:grid-cols-[12rem_1fr]">
        <label className="text-sm">
          対応状況
          <select value={status} onChange={(e) => setStatus(e.target.value as ReportStatus)} className="mt-1 block w-full border p-2">
            {STATUSES.map((value) => <option key={value} value={value}>{REPORT_STATUS_LABELS[value]}</option>)}
          </select>
        </label>
        <label className="text-sm">
          対応記録
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="調査内容と措置" className="mt-1 block w-full border p-2" />
        </label>
        <div className="text-sm md:col-span-2">
          <label htmlFor={`acquirer-reported-${report.id}`}>カード会社への報告日時（日本時間）</label>
          <span className="mt-1 flex flex-wrap gap-2">
            <input
              id={`acquirer-reported-${report.id}`} type="datetime-local" value={reportedAt}
              onChange={(e) => setReportedAt(e.target.value)} className="border p-2"
            />
            <button type="button" onClick={() => setReportedAt(nowAsJstLocalInput())} className="border border-gray-300 px-3">今の日時</button>
            {reportedAt && <button type="button" onClick={() => setReportedAt('')} className="border border-gray-300 px-3">クリア</button>}
          </span>
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <button disabled={saving} onClick={() => void save()} className="bg-pink-600 px-4 py-2 font-semibold text-white disabled:opacity-50">
          {saving ? '保存中...' : '保存'}
        </button>
      </div>
    </article>
  );
}
